import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, TextField, Typography
} from '@mui/material';
import { ArrowBack, PlayArrow, Stop, Replay } from '@mui/icons-material';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';

const MAX_PREIMPRESOS_POR_PEDIDO = 5000;

// Carga masiva de preimpresos: recorre todas las combinaciones caja+tarifa
// preimpresas del evento y llama al mismo endpoint que ya funciona bien con
// lotes de ~400, pero UNA A LA VEZ (nunca en paralelo), para no sumarle carga
// extra al servidor. Reemplaza el "caja por caja" manual.
const DELAY_ENTRE_LOTES_MS = 400;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const comboKey = (cajaId, tarifaId) => `${cajaId}-${tarifaId}`;

const PreimpresosMasivoPanel = ({ eventoId, cajas, onBack }) => {
  const [cantidad, setCantidad] = useState(400);
  const [running, setRunning] = useState(false);
  const [estados, setEstados] = useState({}); // { [comboKey]: 'pendiente'|'generando'|'ok'|'error' }
  const [mensajes, setMensajes] = useState({}); // { [comboKey]: string }
  const detenerRef = useRef(false);

  const combos = useMemo(() => {
    return (cajas || [])
      .filter(c => c.preimpreso)
      .flatMap(c =>
        (c.tarifas || [])
          .filter(t => t.preimpreso)
          .map(t => ({
            cajaId: c.id,
            cajaNombre: c.caja,
            tarifaId: t.id,
            tarifaConcepto: t.concepto,
            actual: t.total_preimpresos || 0,
          }))
      );
  }, [cajas]);

  const pendientesYFallidos = combos.filter(cb => {
    const st = estados[comboKey(cb.cajaId, cb.tarifaId)];
    return !st || st === 'error';
  });

  const completados = combos.filter(cb => estados[comboKey(cb.cajaId, cb.tarifaId)] === 'ok').length;

  const correrLote = async (lista) => {
    const cant = parseInt(cantidad, 10);
    if (!cant || cant <= 0) {
      toast.error("Ingresá una cantidad mayor a 0");
      return;
    }
    if (cant > MAX_PREIMPRESOS_POR_PEDIDO) {
      toast.error(`Se pueden generar hasta ${MAX_PREIMPRESOS_POR_PEDIDO} por vez`);
      return;
    }
    setRunning(true);
    detenerRef.current = false;

    for (const combo of lista) {
      if (detenerRef.current) break;
      const key = comboKey(combo.cajaId, combo.tarifaId);
      setEstados(prev => ({ ...prev, [key]: 'generando' }));

      const res = await api.caja.generarPreimpresos(eventoId, combo.cajaId, {
        idtarifa: combo.tarifaId,
        cantidad: cant,
      });

      if (res.status === "success") {
        setEstados(prev => ({ ...prev, [key]: 'ok' }));
      } else {
        setEstados(prev => ({ ...prev, [key]: 'error' }));
        setMensajes(prev => ({ ...prev, [key]: res.message || "Error desconocido" }));
      }

      if (!detenerRef.current) {
        await sleep(DELAY_ENTRE_LOTES_MS);
      }
    }

    setRunning(false);
  };

  const handleIniciar = () => correrLote(combos);
  const handleReintentar = () => correrLote(pendientesYFallidos);
  const handleDetener = () => { detenerRef.current = true; };

  const estadoChip = (key) => {
    const st = estados[key];
    if (st === 'ok') return <Chip label="OK" size="small" sx={{ backgroundColor: "#e8f5e8", color: "#2e7d32", fontWeight: 600 }} />;
    if (st === 'error') return <Chip label="Error" size="small" sx={{ backgroundColor: "#fdecea", color: "#c62828", fontWeight: 600 }} />;
    if (st === 'generando') return <Chip icon={<CircularProgress size={12} />} label="Generando" size="small" sx={{ backgroundColor: "#e8e8ff", color: "#5656ff", fontWeight: 600 }} />;
    return <Chip label="Pendiente" size="small" sx={{ backgroundColor: "#f0f0f0", color: "#888" }} />;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <IconButton onClick={onBack} size="small" sx={{ color: "#5656ff" }} disabled={running}>
          <ArrowBack />
        </IconButton>
        <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#0b0062" }}>
          Carga masiva de preimpresos
        </Typography>
      </Box>

      <Typography sx={{ fontSize: "13px", color: "#666", mb: 2 }}>
        Suma la misma cantidad al pool de cada caja + tarifa preimpresa, una combinación
        a la vez (nunca en paralelo), para no sobrecargar el servidor. Se detectaron{" "}
        <b>{combos.length}</b> combinaciones caja + tarifa.
      </Typography>

      {combos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, border: '1px dashed #ccc', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
          <Typography color="text.secondary" sx={{ fontSize: "14px" }}>
            No hay cajas con tarifas preimpresas configuradas en este evento.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
            <TextField
              type="number"
              size="small"
              label="Cantidad por combinación"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              inputProps={{ min: 1 }}
              disabled={running}
              sx={{ width: 220 }}
            />
            {!running ? (
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleIniciar}
                sx={{ backgroundColor: "#5656FF", color: "white", textTransform: "none", "&:hover": { backgroundColor: "#4545dd" } }}
              >
                Iniciar carga ({combos.length})
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Stop />}
                onClick={handleDetener}
                sx={{ textTransform: "none" }}
              >
                Detener
              </Button>
            )}
            {!running && pendientesYFallidos.length > 0 && completados > 0 && (
              <Button
                variant="outlined"
                startIcon={<Replay />}
                onClick={handleReintentar}
                sx={{ textTransform: "none", color: "#5656ff", borderColor: "#5656ff" }}
              >
                Reintentar pendientes ({pendientesYFallidos.length})
              </Button>
            )}
          </Box>

          <Typography sx={{ fontSize: "13px", color: "#666", mb: 1 }}>
            Progreso: {completados} / {combos.length}
          </Typography>

          <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", maxHeight: 420, overflowY: "auto" }}>
            {combos.map(combo => {
              const key = comboKey(combo.cajaId, combo.tarifaId);
              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 1, px: 1.5, py: 1, borderBottom: "1px solid #f5f5f5",
                    "&:last-child": { borderBottom: "none" }
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#0b0062" }}>
                      {combo.cajaNombre} · {combo.tarifaConcepto}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#888" }}>
                      Pool actual: {combo.actual}
                      {mensajes[key] ? ` · ${mensajes[key]}` : ""}
                    </Typography>
                  </Box>
                  {estadoChip(key)}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default PreimpresosMasivoPanel;
